// src/pages/CampaignDetails.tsx

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Spinner, Button, Nav, NavItem,
    NavLink, TabContent, TabPane, Alert,
    Table
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import ReactECharts from "echarts-for-react";
import {
    campaignGetRequest,
    campaignFullReportRequest,
    campaignDripReportRequest
} from "store/campaigns/actions";
import { ApplicationState } from "store";
import CampaignRecipients from "./CampaignRecipients";

const CampaignDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<any>();
    const {
        current: campaign,
        loading: campLoading,
        error: campError,
        report,
        dripreport
    } = useSelector((s: ApplicationState) => s.campaign);
    const s = useSelector((s: ApplicationState) => s.campaign);

    const [tab, setTab] = useState<"overview" | "recipients" | "events">("overview");

    useEffect(() => {
        if (!id) return;
        dispatch(campaignGetRequest(id));
        dispatch(campaignFullReportRequest(id));
        dispatch(campaignDripReportRequest(id));
    }, [id, dispatch]);

    if (campLoading) return <Spinner className="m-5" />;
    if (campError || !campaign) return <Alert color="danger">{campError}</Alert>;

    // unpack report
    const {
        total_recipients = 0, sent_count = 0, open_count = 0, click_count = 0, bounce_count = 0,
        send_time_series = [], event_counts = {}
    } = report || {};

    // eCharts option for time series
    const timeOption = {
        xAxis: { type: "category", data: send_time_series.map(p => p.date) },
        yAxis: { type: "value" },
        legend: {
            data: ["sends", "opens", "clicks"],
            textStyle: {
                color: "#7f8fa6",
            },
        },
        tooltip: {},
        series: [
            { name: "sends", type: "line", data: send_time_series.map(p => p.sends) },
            { name: "opens", type: "line", data: send_time_series.map(p => p.opens) },
            { name: "clicks", type: "line", data: send_time_series.map(p => p.clicks) },
        ]
    };

    // eCharts option for pie
    const pieData = Object.entries(event_counts).map(([name, value]) => ({ name, value }));
    const pieOption = {
        tooltip: { trigger: "item" },
        legend: {
            bottom: 0,
            textStyle: {
                color: "#7f8fa6",
            },
        },
        series: [{
            name: "Events", type: "pie", radius: "50%", data: pieData,
            emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: "rgba(0,0,0,0.5)" } }
        }]
    };

    return (
        <Container fluid className="py-5">
            {/* Hero */}
            <Row className="mb-4 align-items-center">
                <Col>
                    <h1 className="fs-1 fw-bold">{campaign.name}</h1>
                    <Badge pill color="info" className="me-2">
                        <i className="ri-mail-send-line me-1" /> {campaign.campaign_type}
                    </Badge>
                    <Badge pill color="secondary">{campaign.status}</Badge>
                </Col>
                <Col className="text-end">
                    <Button tag={Link} to="/campaigns" color="primary">
                        ← Back
                    </Button>
                </Col>
            </Row>

            {/* Tabs */}
            <Nav tabs className="mb-4">
                {["overview", "drip progress", "recipients"].map(t => (
                    <NavItem key={t}>
                        <NavLink
                            className={tab === t ? "active" : ""}
                            style={{ cursor: "pointer", textTransform: "capitalize" }}
                            onClick={() => setTab(t as any)}
                        >{t}</NavLink>
                    </NavItem>
                ))}
            </Nav>
            <TabContent activeTab={tab}>

                {/* OVERVIEW */}
                <TabPane tabId="overview">
                    <Row className="mb-4 gx-3 gy-3 justify-content-between">
                        {[
                            { label: "Total Recipients", value: total_recipients, icon: "ri-group-line" },
                            { label: "Sent", value: sent_count, icon: "ri-send-plane-line" },
                            { label: "Opens", value: open_count, icon: "ri-eye-line" },
                            { label: "Clicks", value: click_count, icon: "ri-mouse-line" },
                            { label: "Bounces", value: bounce_count, icon: "ri-flashlight-line" },
                        ].map(({ label, value, icon }) => (
                            <Col
                                key={label}
                                xs="12"
                                sm="6"
                                md="4"
                                lg // auto-width, 5 per row, fills grid
                                className="d-flex flex-grow-1"
                                style={{ minWidth: 0 }}
                            >
                                <Card className="text-center shadow-sm flex-fill w-100">
                                    <CardBody>
                                        <i className={`${icon} fs-2 mb-2 text-primary`}></i>
                                        <h3 className="fw-bold">{value}</h3>
                                        <div className="text-body">{label}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Row className="gx-4">
                        <Col md="8" className="mb-4">
                            <Card className="h-100 shadow-sm">
                                <CardHeader>Sends & Opens Over Time</CardHeader>
                                <CardBody style={{ height: 300 }}>
                                    <ReactECharts option={timeOption} style={{ height: "100%" }} />
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md="4" className="mb-4">
                            <Card className="h-100 shadow-sm">
                                <CardHeader>Event Distribution</CardHeader>
                                <CardBody style={{ height: 300 }}>
                                    <ReactECharts option={pieOption} style={{ height: "100%" }} />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </TabPane>

                <TabPane tabId="drip progress">
                    {/* Table */}
                    <Card className="shadow-sm mb-3">
                        <CardBody className="bg-light p-0">
                            {!dripreport?.steps || dripreport?.steps?.length === 0 ? (
                                <div className="text-center p-5">
                                    <i className="ri-flow-chart fs-1 text-muted mb-3" />
                                    <h5 className="fw-semibold text-muted">No Drip Step Metrics found</h5>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Step</th>
                                                <th>Total</th>
                                                <th>Sent</th>
                                                <th>Opened</th>
                                                <th>Clicked</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dripreport?.steps?.length ? dripreport?.steps?.map((m: any) => (
                                                <tr key={m.step_index}>
                                                    <td>{m.step_index}</td>
                                                    <td>{m.total}</td>
                                                    <td>{m.sent}</td>
                                                    <td>{m.opened}</td>
                                                    <td>{m.clicked}</td>
                                                </tr>
                                            )) : null}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Pagination */}
                    {/* {pagination && (
                    <PaginationControls
                      pagination={{
                        page: pagination.page,
                        size: pagination.size,
                        elements: pagination.elements,
                        first: pagination.first,
                        last: pagination.last,
                  pages: pagination.pages
                      }}
                      pageChanged={(which) => {
                        let next = pagination.page;
                        if (which === "first") next = 0;
                        else if (which === "prev") next = Math.max(0, next - 1);
                        else if (which === "next") next = Math.min(pagination.pages - 1, next + 1);
                        else if (which === "last") next = pagination.pages - 1;
                        else if (typeof which === "number") next = which;
                        fetch(next, pagination.size);
                      }}
                      onChangeInSelect={(e) => {
                        const newSize = Number(e.target.value);
                        fetch(0, newSize);
                      }}
                      pageSize={pagination.size}
                    />
                  )} */}
                </TabPane>

                {/* RECIPIENTS */}
                <TabPane tabId="recipients">
                    <CampaignRecipients campaignId={id!} />
                </TabPane>

                {/* EVENTS */}
                {/* <TabPane tabId="events">
          {eventsLoading ? <Spinner /> : (
            <Card className="shadow-sm">
              <CardHeader>Events</CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive" style={{ maxHeight: 400 }}>
                  <Table hover className="mb-0">
                    <thead className="bg-light position-sticky top-0">
                      <tr><th>Time</th><th>Type</th><th>Recipient</th></tr>
                    </thead>
                    <tbody>
                      {events.map(e => (
                        <tr key={e.id}>
                          <td>{new Date(e.eventTime).toLocaleString()}</td>
                          <td>{e.eventType}</td>
                          <td>{e.recipientId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {!!eventsPagination && eventsPagination.pages > 1 && (
                  <div className="p-3 d-flex justify-content-end">
                    <Button size="sm" disabled={eventsPagination.first} onClick={() => setEPage(0)}>First</Button>
                    <Button size="sm" disabled={eventsPagination.first} className="ms-2" onClick={() => setEPage(p => p - 1)}>Prev</Button>
                    <span className="mx-3">{eventsPagination.page + 1}/{eventsPagination.pages}</span>
                    <Button size="sm" disabled={eventsPagination.last} className="me-2" onClick={() => setEPage(p => p + 1)}>Next</Button>
                    <Button size="sm" disabled={eventsPagination.last} onClick={() => setEPage(eventsPagination.pages - 1)}>Last</Button>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </TabPane> */}
            </TabContent>
        </Container>
    );
};

export default CampaignDetails;
